// ЕЖВ.1: экран /daily-login.
//
// Шапка: «Ежедневный вход».
// Под ней — статус серии + ряд из 7 кружков (дни недели).
// Кнопка «Забрать +N Скр» если не клеймилось сегодня.
// Снизу — мини-статистика.

import { useEffect, useState, useMemo } from 'react';
import { ScreenLayout } from '../components/ScreenLayout';
import { useTelegram } from '../telegram/useTelegram';
import { useGameStore } from '../store/useGameStore';
import { ScriptsIcon } from '../components/ScriptsIcon';
import { getDailyLoginStatus, claimDailyLogin } from '../api/client';
import { useTooltipFlow } from '../tutorial/useTooltipFlow';
import { useTr } from '../i18n/useTr';

const REWARDS = [3, 4, 5, 6, 7, 8, 15];

export function DailyLoginScreen() {
  const { hapticTap, hapticSuccess, hapticError, alert } = useTelegram();
  const dailyLogin = useGameStore(s => s.dailyLogin);
  const setDailyLogin = useGameStore(s => s.setDailyLogin);
  const addScripts = useGameStore(s => s.addScripts);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  // ФТУЕ.6: tooltip-флоу первого входа в Ежедневный вход (по диздоку §11.5).
  const tr = useTr();
  const SI = () => (
    <span style={{ display: 'inline-flex', verticalAlign: '-2px', margin: '0 1px' }}>
      <ScriptsIcon size={14} />
    </span>
  );
  useTooltipFlow('daily', () => [
    {
      id: 'd-intro',
      coach: {
        title: tr('Ежедневный вход', 'Daily login'),
        body: tr('ru', 'en') === 'en' ? (
          <>
            Drop in every day — claim Scripts (<SI />). 7-day streak:
            day 1 — 3<SI />, day 2 — 4<SI />, then 5/6/7/8, day 7 — <b>15</b><SI />
            bonus for a full week.
          </>
        ) : (
          <>
            Заходи каждый день — забирай Скрипты (<SI />). Серия из 7 дней:
            день 1 — 3<SI />, день 2 — 4<SI />, далее 5/6/7/8, день 7 — <b>15</b><SI />
            бонусом за полную неделю.
          </>
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'd-streak',
      coach: {
        title: tr('Серия не прерывается', 'Keep the streak'),
        body: tr('ru', 'en') === 'en' ? (
          <>
            Days roll over at <b>00:00 UTC</b>. Miss a day — the streak resets
            and starts over from day 1. Drop in and claim — streak continues.
          </>
        ) : (
          <>
            Сутки считаются по <b>UTC</b> (00:00). Если пропустишь день — серия
            обнулится и пойдёт заново с дня 1. Зашёл и забрал — серия продолжается.
          </>
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'd-reminder',
      coach: {
        title: tr('Напоминание', 'Reminder'),
        body: tr(
          'В Профиле → ⚙ → «Уведомления» можно включить пуш-напоминание о ежедневном входе. Telegram пришлёт сообщение если не зашёл за день.',
          'In Profile → ⚙ → «Notifications» you can enable a push reminder for daily login. Telegram will ping you if you skip the day.'
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);

  // Перезапрос статуса при маунте.
  useEffect(() => {
    (async () => {
      try {
        const s = await getDailyLoginStatus();
        setDailyLogin(s);
      } catch { /* offline */ }
    })();
  }, [setDailyLogin]);

  // Таймер обратного отсчёта до 00:00 UTC — обновляется каждую секунду.
  useEffect(() => {
    if (!dailyLogin?.claimed_today) return;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [dailyLogin?.claimed_today]);

  const tomorrow = useMemo(() => {
    void tick;
    const now = new Date();
    const utcMidnight = Date.UTC(
      now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0
    );
    const diff = Math.max(0, utcMidnight - now.getTime());
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1000);
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  }, [tick]);

  const isEn = tr('ru', 'en') === 'en';
  const pluralWord = (n: number): string => isEn ? pluralEn(n) : pluralRu(n);

  const onClaim = async () => {
    if (!dailyLogin || dailyLogin.claimed_today) return;
    hapticTap();
    setBusy(true);
    try {
      const r = await claimDailyLogin();
      hapticSuccess();
      addScripts(r.reward);
      alert(tr(
        `+${r.reward} Скриптов в копилку. Серия: ${r.current_streak_length} ${pluralRu(r.current_streak_length)}.`,
        `+${r.reward} Scripts banked. Streak: ${r.current_streak_length} ${pluralEn(r.current_streak_length)}.`
      ));
      const fresh = await getDailyLoginStatus();
      setDailyLogin(fresh);
    } catch (e: any) {
      hapticError();
      alert(tr(`Не удалось: ${e?.message || 'ошибка'}`, `Failed: ${e?.message || 'error'}`));
    } finally {
      setBusy(false);
    }
  };

  if (!dailyLogin) {
    return (
      <ScreenLayout title={tr('ЕЖЕДНЕВНЫЙ ВХОД', 'DAILY LOGIN')} backTo="/home" showHelp={false}>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t-md)' }}>{tr('Загрузка…', 'Loading…')}</div>
      </ScreenLayout>
    );
  }

  // Какой день будет «текущим» для подсветки.
  // Если claimed_today=true → подсветить дни 1..(current_day-1) или 1..7 если только что забрали 7.
  // current_day в стейте — это «который забирать в СЛЕДУЮЩИЙ клейм».
  const dayJustClaimed = dailyLogin.claimed_today
    ? (dailyLogin.current_day === 1 ? 7 : dailyLogin.current_day - 1)
    : null;
  const todayDay = dailyLogin.claimed_today ? null : dailyLogin.current_day;

  const headerText = (() => {
    if (dailyLogin.claimed_today) {
      return tr(
        `Серия: ${dailyLogin.current_streak_length} ${pluralRu(dailyLogin.current_streak_length)} · следующая через ${tomorrow}`,
        `Streak: ${dailyLogin.current_streak_length} ${pluralEn(dailyLogin.current_streak_length)} · next in ${tomorrow}`
      );
    }
    if (dailyLogin.streak_alive && dailyLogin.current_streak_length > 0) {
      return tr(
        `Серия: ${dailyLogin.current_streak_length} ${pluralRu(dailyLogin.current_streak_length)} · сегодня доступен день ${dailyLogin.current_day}`,
        `Streak: ${dailyLogin.current_streak_length} ${pluralEn(dailyLogin.current_streak_length)} · day ${dailyLogin.current_day} available today`
      );
    }
    if (!dailyLogin.streak_alive) {
      return tr('Серия сброшена. Зайди сегодня — начни заново.', 'Streak reset. Drop in today — start over.');
    }
    return tr(
      'Первый день в Корпах. Забери награду и начни серию.',
      'First day in Corps. Claim the reward and start a streak.'
    );
  })();

  const nextReward = REWARDS[(dailyLogin.current_day - 1) % 7];

  return (
    <ScreenLayout title={tr('ЕЖЕДНЕВНЫЙ ВХОД', 'DAILY LOGIN')} backTo="/home" showHelp={false}>
      <div style={{
        padding: 14,
        background: 'rgba(255,255,255,0.03)',
        fontSize: 13, color: 'var(--t-md)',
        lineHeight: 1.4, marginBottom: 14
      }}>
        {headerText}
      </div>

      {/* Ряд из 7 дней. У каждого 2 строки: подпись «День N» сверху +
          кружок с наградой снизу. Финал (день 7) подсвечен амбером. */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 4, padding: '12px 4px', marginBottom: 14
      }}>
        {[1, 2, 3, 4, 5, 6, 7].map(d => {
          const passed = dayJustClaimed !== null && d <= dayJustClaimed;
          const isToday = d === todayDay;
          const isFinal = d === 7;
          const reward = REWARDS[d - 1];
          return (
            <DayCircle
              key={d}
              day={d}
              reward={reward}
              passed={passed}
              isToday={isToday}
              isFinal={isFinal}
              labelDay={tr('День', 'Day')}
              labelFinal={tr('Финал', 'Final')}
            />
          );
        })}
      </div>

      {/* Кнопка забрать */}
      <button
        onClick={onClaim}
        disabled={dailyLogin.claimed_today || busy}
        style={{
          width: '100%', padding: 14,
          background: dailyLogin.claimed_today ? 'var(--bg-3, #171723)' : 'var(--neon-li)',
          color: dailyLogin.claimed_today ? 'var(--t-md)' : 'var(--bg, #07070B)',
          border: 'none',
          fontSize: 13, fontWeight: 700,
          letterSpacing: 1, cursor: dailyLogin.claimed_today ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
        }}
      >
        {dailyLogin.claimed_today
          ? <>{tr('СЛЕДУЮЩАЯ ЧЕРЕЗ', 'NEXT IN')} {tomorrow}</>
          : <>{tr('ЗАБРАТЬ', 'CLAIM')} +{nextReward}<ScriptsIcon size={14} /></>}
      </button>

      <div style={{
        marginTop: 14,
        padding: 12,
        background: 'rgba(255,255,255,0.02)',
        fontSize: 12, color: 'var(--t-md)',
        lineHeight: 1.55
      }}>
        {tr(
          'Заходи каждый день UTC, чтобы держать серию. Пропустил день — серия сбросится на день 1.',
          'Drop in every UTC day to keep the streak. Skip a day — streak resets to day 1.'
        )}
        <br /><br />
        {tr('За 4 недели регулярных входов получишь', "Over 4 weeks of regular logins you'll get")}{' '}
        <span style={{ color: 'var(--neon-li)', fontWeight: 600, whiteSpace: 'nowrap' }}>
          ~190 <ScriptsIcon size={12} />
        </span>
        {' '}— {tr('этого хватит на', 'enough for')}{' '}
        <span style={{ color: 'var(--neon-li)', fontWeight: 600 }}>
          {tr('1 Основной общий ШК', '1 Standard common CC')}
        </span>
        . {tr('Прокачивай коллекцию без затрат.', 'Grow your collection without spending.')}
      </div>

      <div style={{
        marginTop: 14,
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10
      }}>
        <div className="cut-s" style={{ padding: 10, background: 'var(--bg-2, #101018)' }}>
          <div style={{ fontSize: 10, color: 'var(--t-lo)', letterSpacing: 1 }}>{tr('ЛУЧШАЯ СЕРИЯ', 'BEST STREAK')}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--neon-am)', marginTop: 4 }}>
            {dailyLogin.longest_streak}
          </div>
          <div style={{ fontSize: 10, color: 'var(--t-md)' }}>
            {pluralWord(dailyLogin.longest_streak)}
          </div>
        </div>
        <div className="cut-s" style={{ padding: 10, background: 'var(--bg-2, #101018)' }}>
          <div style={{ fontSize: 10, color: 'var(--t-lo)', letterSpacing: 1 }}>{tr('ВСЕГО НАГРАД', 'TOTAL CLAIMS')}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--neon-cy)', marginTop: 4 }}>
            {dailyLogin.total_claims}
          </div>
          <div style={{ fontSize: 10, color: 'var(--t-md)' }}>{tr('забрано', 'claimed')}</div>
        </div>
      </div>
    </ScreenLayout>
  );
}

function DayCircle({
  day, reward, passed, isToday, isFinal, labelDay, labelFinal
}: {
  day: number; reward: number;
  passed: boolean; isToday: boolean; isFinal: boolean;
  labelDay: string;   // 'День' / 'Day'
  labelFinal: string; // 'Финал' / 'Final'
}) {
  const size = isFinal ? 50 : 42;
  let bg = 'transparent';
  let border = '0.5px solid var(--t-mute)';
  let color: string = 'var(--t-md)';
  let content: any = (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 1,
      fontSize: isFinal ? 14 : 12, fontWeight: 700
    }}>
      +{reward}<ScriptsIcon size={isFinal ? 12 : 10} />
    </span>
  );
  let labelColor: string = 'var(--t-lo)';

  if (passed) {
    bg = 'var(--neon-li)';
    border = '0.5px solid var(--neon-li)';
    color = 'var(--bg, #07070B)';
    content = <span style={{ fontSize: 22, fontWeight: 700 }}>✓</span>;
    labelColor = 'var(--neon-li)';
  } else if (isToday) {
    border = `1.5px solid var(--neon-li)`;
    color = 'var(--neon-li)';
    labelColor = 'var(--neon-li)';
  }
  if (isFinal && !passed) {
    border = `1.5px solid #FFB13D`;
    color = '#FFB13D';
    labelColor = '#FFB13D';
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 5,
      flex: '0 0 auto',
      minWidth: size
    }}>
      {/* Верхняя строка: «День N» (или «Финал» для 7-го) */}
      <div style={{
        fontSize: 9, fontWeight: 600,
        color: labelColor,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap'
      }}>
        {isFinal ? labelFinal : `${labelDay} ${day}`}
      </div>
      {/* Нижняя строка: кружок с наградой (или галкой если passed) */}
      <div style={{
        width: size, height: size,
        borderRadius: '50%',
        border, background: bg, color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: isToday ? 'dl-pulse 2s ease-in-out infinite' : undefined,
        flexShrink: 0
      }}>{content}</div>
    </div>
  );
}

function pluralRu(n: number): string {
  const last = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'дней';
  if (last === 1) return 'день';
  if (last >= 2 && last <= 4) return 'дня';
  return 'дней';
}

function pluralEn(n: number): string {
  return n === 1 ? 'day' : 'days';
}
